'use client';

import { useRouter, useSearchParams } from 'next/navigation';
import { useCallback, useEffect } from 'react';
import { trackStandaloneChange } from '@/modules/googleAnalytics/helpers';
import {
  ANROID_AND_IOS_APP_SOURCE,
  MAIN_APP_IDENTIFIER,
  MAIN_PWA_IDENTIFIER,
} from '../constants';
import { useTrackingUserParamsStore } from '../store';

enum TrackingParamKey {
  AppId = 'app_id',
  PwaId = 'pwa_id',
  Source = 'source',
}

export const useTrackAppOrPwaId = () => {
  const searchParams = useSearchParams();
  const router = useRouter();

  const queryAppId = searchParams.get(TrackingParamKey.AppId) ?? undefined;
  const queryPwaId = searchParams.get(TrackingParamKey.PwaId) ?? undefined;
  const querySource = searchParams.get(TrackingParamKey.Source) ?? undefined;

  const { app_id, pwa_id, source, setTrackingUserParams } =
    useTrackingUserParamsStore((state) => ({
      app_id: state.app_id,
      pwa_id: state.pwa_id,
      source: state.source,
      setTrackingUserParams: state.setTrackingUserParams,
    }));

  const checkPwaAppOwner = useCallback(
    (isStandalone: boolean) => {
      if (isStandalone) {
        let shouldClean = false;

        if (queryAppId && !app_id) {
          setTrackingUserParams({ app_id: queryAppId });
          shouldClean = true;
        }

        if (queryPwaId && !pwa_id) {
          setTrackingUserParams({ pwa_id: queryPwaId });
          shouldClean = true;
        }

        if (
          !(queryAppId || queryPwaId || pwa_id || app_id) &&
          typeof document?.referrer !== 'undefined'
        ) {
          const isOwnPwaByReferer =
            document.referrer === '' ||
            document.referrer.includes(window.location.host);
          setTrackingUserParams({
            pwa_id: isOwnPwaByReferer ? MAIN_PWA_IDENTIFIER : document.referrer,
          });
        }

        if (shouldClean) {
          const url = new URL(window.location.href);
          url.searchParams.delete(TrackingParamKey.AppId);
          url.searchParams.delete(TrackingParamKey.PwaId);

          router.replace(url.pathname + url.search);
        }
      } else {
        setTrackingUserParams({ pwa_id: undefined, app_id: undefined });
      }
    },
    [queryAppId, queryPwaId, setTrackingUserParams]
  );

  useEffect(() => {
    const unsubscribe = trackStandaloneChange(checkPwaAppOwner);
    return unsubscribe;
  }, [checkPwaAppOwner]);

  useEffect(() => {
    if (querySource && querySource === ANROID_AND_IOS_APP_SOURCE && !source) {
      setTrackingUserParams({
        app_id: MAIN_APP_IDENTIFIER,
        source: querySource,
      });

      const url = new URL(window.location.href);
      url.searchParams.delete(TrackingParamKey.Source);
      router.replace(url.pathname + url.search);
    }
  }, [querySource]);

  return { app_id, pwa_id };
};
